import { createAsyncThunk } from "@reduxjs/toolkit";

import * as checkoutAPI from "../../../api/checkout";
import { IUser } from "../../../models/user";
import { IReduxState } from "../reducers.interfaces";
import { serializeCheckoutError } from "../utils";
import { completeOrderPayment } from "./completeOrderPayment";
import { getOrderPayment } from "./placeOrder";

/**
 * Arguments for completeDeferredPayment thunk.
 */
interface CompleteDeferredPaymentArgs {
    user: IUser | null;
    basketToken: string;
    orderToken: string;
}

/**
 * Complete a deferred payment order.
 *
 * This thunk handles completing payment for orders that were previously
 * created but had payment deferred (e.g., for Affirm financing where
 * the user needs to complete the financing application). It's similar
 * to placeOrder but uses a different API endpoint and tokens.
 *
 * The flow:
 * 1. Submit payment info for the existing order
 * 2. Delegate payment state checking to completeOrderPayment
 *
 * @param {IUser | null} args.user - Current user (or null for guest)
 * @param {string} args.basketToken - Token identifying the basket
 * @param {string} args.orderToken - Token identifying the order
 * @returns {Promise<void>} Promise resolving when payment is completed or flow initiated
 *
 * @example
 * // Complete deferred payment from URL params
 * dispatch(completeDeferredPayment({
 *   user: currentUser,
 *   basketToken: urlParams.get('basket-token'),
 *   orderToken: urlParams.get('order-token')
 * }));
 *
 * @throws {ICheckoutHTTPError} If payment completion fails or payment is declined
 *
 * @sideEffects
 * - Reads payment method data from state
 * - Makes API call to complete payment via checkoutAPI.completeDeferredPayment
 * - Updates `data.order` with order response via updateOrder action
 * - Delegates payment completion to completeOrderPayment thunk
 */
export const completeDeferredPayment = createAsyncThunk(
    "checkout/completeDeferredPayment",
    async (
        args: CompleteDeferredPaymentArgs,
        { getState, dispatch },
    ): Promise<void> => {
        const state = getState() as { checkout: IReduxState };
        const checkoutState = state.checkout;

        const payment = getOrderPayment(checkoutState);
        const order = await checkoutAPI.completeDeferredPayment(
            args.basketToken,
            args.orderToken,
            payment,
        );

        // Import actions dynamically to avoid circular dependency
        const { updateOrder } = await import("../reducers");
        dispatch(updateOrder(order));

        // Complete the payment flow (check states, handle 3DS, redirect)
        await dispatch(completeOrderPayment({ user: args.user })).unwrap();
    },
    { serializeError: serializeCheckoutError },
);
